import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, FolderOpen } from 'lucide-react';
import { portalApi } from '../lib/api';
import { queueUpload, getPendingUploads, type QueuedUpload } from '../lib/offline';

interface Props {
  isOnline: boolean;
}

export function UploadPage({ isOnline }: Props) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'offline'; text: string } | null>(null);
  const [pendingCount, setPendingCount] = useState(0);

  // Refresh pending count on mount, visibility change, and online events
  useEffect(() => {
    const refresh = () => getPendingUploads().then((uploads) => setPendingCount(uploads.length));
    refresh();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const onOnline = () => refresh();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  async function handleFile(file: File) {
    setUploading(true);
    setMessage(null);

    try {
      if (isOnline) {
        await portalApi.uploadDocument(file);
        setMessage({ type: 'success', text: t('portal.uploadSuccess') });
      } else {
        await queueUpload(file);
        const pending = await getPendingUploads();
        setPendingCount(pending.length);
        setMessage({ type: 'offline', text: t('portal.queuedOffline') });
      }
    } catch {
      // If online upload fails, queue offline
      if (isOnline) {
        try {
          await queueUpload(file);
          const pending = await getPendingUploads();
          setPendingCount(pending.length);
          setMessage({ type: 'offline', text: t('portal.queuedOffline') });
        } catch {
          setMessage({ type: 'error', text: t('portal.uploadError') });
        }
      } else {
        setMessage({ type: 'error', text: t('portal.uploadError') });
      }
    } finally {
      setUploading(false);
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (files && files.length > 0) {
      // Handle multiple files
      Array.from(files).forEach(handleFile);
    }
    // Reset input
    e.target.value = '';
  }

  return (
    <div className="p-4 space-y-6">
      <div className="text-center pt-6 pb-4">
        <h1 className="text-xl font-bold">{t('portal.uploadTitle')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('portal.uploadDescription')}</p>
      </div>

      {/* Upload buttons */}
      <div className="space-y-3">
        <button
          onClick={() => cameraInputRef.current?.click()}
          disabled={uploading}
          className="w-full upload-cta-primary rounded-[14px] py-4 font-bold text-base shadow-lifted flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          <Camera className="h-5 w-5" />
          {uploading ? t('portal.uploading') : t('portal.takePhoto')}
        </button>

        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full rounded-[14px] py-4 border-2 border-dashed border-border hover:border-primary/30 bg-surface-2 text-foreground font-medium text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          <FolderOpen className="h-5 w-5" />
          {t('portal.chooseFile')}
        </button>

        {/* Hidden file inputs */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleInputChange}
          className="hidden"
          multiple
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf,.jpg,.jpeg,.png,.heic"
          onChange={handleInputChange}
          className="hidden"
          multiple
        />
      </div>

      {/* Status message */}
      {message && (
        <div
          className={`p-3 rounded-[12px] text-sm text-center ${
            message.type === 'success'
              ? 'bg-success-soft text-success'
              : message.type === 'offline'
              ? 'bg-warning-soft text-warning'
              : 'bg-danger-soft text-destructive'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Pending uploads indicator */}
      {pendingCount > 0 && (
        <div className="p-3 rounded-[12px] bg-warning-soft text-warning text-sm text-center">
          {t('portal.pendingUploads')}: {pendingCount}
        </div>
      )}
    </div>
  );
}
